import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TemplatesService } from '../../../services/templates.service';
import { TemplateVersion } from '../../../models/TemplateVersion';
import { TableAction } from '../../../shared/components/table/table.component';
import { ErrorService } from '../../../shared/services/error.service';

@Component({
  selector: 'app-template-versions',
  templateUrl: './template-versions.component.html',
  styleUrls: ['./template-versions.component.css']
})
export class TemplateVersionsComponent implements OnInit, OnDestroy {
  templateId!: number;
  versions: TemplateVersion[] = [];
  isLoading = false;
  isUploading = false;
  selectedFile: File | null = null;

  private destroy$ = new Subject<void>();

  // Configuração da tabela genérica
  columns = [
    { titulo: 'Versão', label: 'versionNumber', formatter: (value: number) => `v${value}` },
    { titulo: 'Status', label: 'status' },
    { titulo: 'Tamanho', label: 'fileSizeBytes', formatter: (value: number) => value ? `${(value / 1024).toFixed(2)} KB` : '-' },
    { titulo: 'Arquivo', label: 'storageKey', formatter: (value: string) => value || 'arquivo.docx' }
  ];

  actions: TableAction[] = [
    {
      label: 'Visualizar',
      icon: 'fa-solid fa-eye',
      action: 'view',
      class: 'btn-view',
      visible: true
    },
    {
      label: 'Editar',
      icon: 'fa-solid fa-edit',
      action: 'edit',
      class: 'btn-edit',
      visible: false
    },
    {
      label: 'Download',
      icon: 'fa-solid fa-download',
      action: 'download',
      class: 'btn-export',
      visible: false
    }
  ];

  constructor(
    private route: ActivatedRoute, 
    private router: Router, 
    private templatesService: TemplatesService,
    private messageService: ErrorService
  ) {}

  ngOnInit(): void {
    this.templateId = Number(this.route.snapshot.paramMap.get('id')) || 0;
    if (this.templateId === 0) {
      this.messageService.showErrorMessage('ID do template inválido.');
      this.goBack();
      return;
    }
    this.loadVersions();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadVersions(): void {
    this.isLoading = true;
    this.templatesService.listVersions(this.templateId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.versions = res;
          this.isLoading = false;
        },
        error: (error) => {
          console.error('Erro ao carregar versões:', error);
          this.isLoading = false;
          this.messageService.showErrorMessage('Erro ao carregar versões. Tente novamente.');
        }
      });
  }

  onActionClick(event: { action: string; item: TemplateVersion }): void {
    switch (event.action) {
      case 'view':
        this.view(event.item);
        break;
      case 'edit':
        this.edit(event.item);
        break;
      case 'download':
        this.download(event.item);
        break;
      default:
        break;
    }
  }

  download(version: TemplateVersion): void {
    if (!version.storageUrl) {
      this.messageService.showErrorMessage('URL de armazenamento não disponível.');
      return;
    }
    window.open(version.storageUrl, '_blank');
  }

  view(version: TemplateVersion): void {
    if (!version.storageUrl) {
      this.messageService.showErrorMessage('Arquivo não disponível para visualização.');
      return;
    }

    // Office web viewer para pré-visualização de docx no navegador
    const officeViewer = `https://view.officeapps.live.com/op/view.aspx?src=${encodeURIComponent(version.storageUrl)}`;
    window.open(officeViewer, '_blank');
  }

  edit(version: TemplateVersion): void {
    this.router.navigate(['/documents/templates', this.templateId, 'edit', version.id]);
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0];

    if (!file) {
      this.selectedFile = null;
      return;
    }

    // Validação de arquivo
    const allowedExtensions = ['.docx', '.doc'];
    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();

    if (!allowedExtensions.includes(fileExtension)) {
      this.messageService.showErrorMessage('Apenas arquivos .doc e .docx são permitidos.');
      this.selectedFile = null;
      return;
    }

    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      this.messageService.showErrorMessage('Arquivo muito grande. Máximo de 10MB permitido.');
      this.selectedFile = null;
      return;
    }

    this.selectedFile = file;
  }

  saveNewVersion(): void {
    if (!this.selectedFile) {
      this.messageService.showErrorMessage('Selecione um arquivo antes de salvar.');
      return;
    }

    this.isUploading = true;
    const fileToUpload = this.selectedFile;
    const storageKey = fileToUpload.name.endsWith('.docx') 
      ? fileToUpload.name 
      : `${fileToUpload.name}.docx`;

    this.templatesService.uploadDocx(this.templateId, fileToUpload, storageKey)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.selectedFile = null;
          this.loadVersions();
          this.messageService.showInfoMessage('Versão salva com sucesso!');
        },
        error: (error) => {
          console.error('Erro ao salvar versão:', error);
          this.messageService.showErrorMessage('Erro ao salvar versão. Tente novamente.');
        },
        complete: () => {
          this.isUploading = false;
        }
      });
  }

  goBack(): void {
    this.router.navigate(['/documents/templates']);
  }
}


